import { InputAdornment } from '@mui/material'
import {
  DateTimeValidationError,
  LocalizationProvider,
  PickerChangeHandlerContext,
  PickersShortcutsItem,
} from '@mui/x-date-pickers'
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon'
import {
  DateRange as MuiDateRange,
  DateRangePicker,
  DateRangePickerProps,
  SingleInputDateRangeField,
} from '@mui/x-date-pickers-pro'
import { DateTime } from 'luxon'
import React, { useState } from 'react'

import { useComplatibleLocale } from '../../hooks/useCompatibleLocale'
import { useRtlLocaleCheck } from '../../hooks/useRtlLocaleCheck'
import { createComponentWrapperWithRef, styled, THEME_CONST, uiTokens } from '../../theme'
import { MsdkStyledComponent } from '../../utils'
import { ButtonIcon } from '../Button'
import { IconSelector } from '../Icons'

const StyledMuiDateRangePicker: MsdkStyledComponent<DateRangePickerProps<any>> = styled(DateRangePicker)`
  .MuiInputBase-root {
    border-radius: ${THEME_CONST.inputBorderRadius}px;
    width: 100%;
  }
  .MuiInputBase-input {
    padding: 9px;
    width: 100%;
  }
  .MuiFormControl-root {
    width: 100%;
  }
`

const StyledNovaDateRangePicker: MsdkStyledComponent<DateRangePickerProps<any>> = styled(DateRangePicker)`
  .MuiInputBase-root {
    border-radius: ${uiTokens.radius.large}px;
  }
  .MuiSvgIcon-root {
    color: ${uiTokens.uiContent.accent};
  }
  &:hover .MuiInputLabel-root {
    color: ${uiTokens.uiContent.accent};
  }

  .MuiInputLabel-root.Mui-focused {
    color: ${uiTokens.uiContent.accent};
  }
`

export const StyledDateRangePicker = createComponentWrapperWithRef(
  {
    mui: StyledMuiDateRangePicker,
    nova: StyledNovaDateRangePicker,
  },
  'StyledDateRangePicker',
)

export type DateRangePickerShortcuts = PickersShortcutsItem<MuiDateRange<DateTime>>[]
export type DateRange = MuiDateRange<DateTime<boolean> | null>

type Props = {
  disabled?: boolean
  label: React.ReactNode
  locale?: string
  onChange: (value: DateRange | undefined, context?: PickerChangeHandlerContext<DateTimeValidationError>) => void
  shortcuts?: PickersShortcutsItem<MuiDateRange<DateTime>>[]
  value: DateRange | undefined
  size?: 'small' | 'medium'
}

export function RangeDatePicker(props: Props) {
  const { disabled, label, locale = DateTime.local().locale, onChange, shortcuts, value, size } = props

  const [open, setOpen] = useState<boolean>(false)
  const compatibleLocale = useComplatibleLocale(locale)
  const isRtl = useRtlLocaleCheck(locale)

  function handleDateChange(newDates: DateRange | undefined) {
    if (newDates) {
      onChange?.(newDates)
    }
  }

  function handleOpen() {
    setOpen(true)
  }

  function handleClose() {
    setOpen(false)
  }

  function handleIconClick() {
    setOpen((prev) => !prev)
  }

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale={compatibleLocale}>
      <StyledDateRangePicker
        disabled={disabled}
        label={label}
        slots={{
          field: SingleInputDateRangeField,
        }}
        slotProps={{
          field: {
            readOnly: true,
            disabled,
          },
          textField: {
            // onClick: () => null,
            InputProps: {
              size,
              readOnly: true,
              dir: isRtl ? 'rtl' : undefined,
              endAdornment: (
                <InputAdornment position="end" style={{ cursor: 'pointer' }}>
                  <ButtonIcon onClick={handleIconClick}>
                    <IconSelector iconName="DateRange" />
                  </ButtonIcon>
                </InputAdornment>
              ),
            },
          },
          shortcuts: {
            items: shortcuts,
          },
          actionBar: { actions: [] },
        }}
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        calendars={2}
        value={value}
        onChange={handleDateChange}
        maxDate={DateTime.now()}
      />
    </LocalizationProvider>
  )
}
